/*
 * SalesTab — inline Sales analytics panel for the Command Center.
 *
 * Extracted from SalesAnalytics.jsx so the "Sales" tab on /admin/analytics
 * renders the same UI as /admin/sales without leaving the Command Center
 * (avoiding the mount/unmount round-trip that was breaking the data pipeline
 * when navigating back).
 *
 * Props:
 *   range   — "7d" | "30d" | "90d" | "1y" | "total"  (controlled by parent)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, Users, AlertTriangle,
  Activity, PieChart as PieIcon, ShoppingBag, LineChart as LineIcon,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell, Legend, Area, AreaChart,
} from "recharts";
import TrafficTrendChart from "./TrafficTrendChart";

const GOLD = "#C5A55A";
const CYAN = "#22d3ee";
const GREEN = "#22c55e";
const PIE_COLORS = [CYAN, GREEN, GOLD, "#c084fc", "#f97316", "#3b82f6", "#ec4899", "#84cc16", "#8b5cf6", "#14b8a6"];

const fmtUsd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();

const KpiTile = ({ label, value, sub, tone = "cyan", testId }) => {
  const toneMap = {
    cyan: "text-cyan-300", green: "text-emerald-300", amber: "text-amber-300",
    red: "text-red-300", gold: "text-[#C5A55A]",
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#111113] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`text-xl font-mono mt-1 ${toneMap[tone] || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, action, children, testId }) => (
  <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />} {title}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

export default function SalesTab({ range = "30d" }) {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [productMix, setProductMix] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [forecast, setForecast] = useState({ forecast: [], avg_daily: 0, std: 0 });
  const [refunds, setRefunds] = useState({ timeline: [], top_refunders: [], total_usd: 0 });
  const [conversion, setConversion] = useState({ visitors: 0, paying_customers: 0, conversion_pct: 0 });
  const [ltv, setLtv] = useState([]);
  const [churn, setChurn] = useState({ cancelled_or_scheduled: 0, mrr_at_risk_usd: 0, at_risk: [] });
  const [cohorts, setCohorts] = useState([]);
  const [trafficTimeline, setTrafficTimeline] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [k, tl, mix, tp, tc, fc, rf, cv, lt, ch, co, tt] = await Promise.all([
        authAxios.get(`/analytics/sales/kpis`),
        authAxios.get(`/analytics/sales/timeline?range=${range}`),
        authAxios.get(`/analytics/sales/product-mix?range=${range}`),
        authAxios.get(`/analytics/sales/top-products?range=${range}&limit=10`),
        authAxios.get(`/analytics/sales/top-customers?range=total&limit=20`),
        authAxios.get(`/analytics/sales/forecast?days=14`),
        authAxios.get(`/analytics/sales/refunds?range=${range}&limit=10`),
        authAxios.get(`/analytics/sales/conversion?range=${range}`),
        authAxios.get(`/analytics/sales/ltv-by-source`),
        authAxios.get(`/analytics/sales/churn?days=30`),
        authAxios.get(`/analytics/sales/cohorts?weeks=8`),
        authAxios.get(`/analytics/command/timeline?range=${range}`),
      ]);
      setKpis(k.data);
      setTimeline(tl.data?.timeline || []);
      setProductMix(mix.data?.slices || []);
      setTopProducts(tp.data?.products || []);
      setTopCustomers(tc.data?.customers || []);
      setForecast(fc.data || { forecast: [], avg_daily: 0, std: 0 });
      setRefunds(rf.data || { timeline: [], top_refunders: [], total_usd: 0 });
      setConversion(cv.data || { visitors: 0, paying_customers: 0, conversion_pct: 0 });
      setLtv(lt.data?.sources || []);
      setChurn(ch.data || { cancelled_or_scheduled: 0, mrr_at_risk_usd: 0, at_risk: [] });
      setCohorts(co.data?.cohorts || []);
      setTrafficTimeline(tt.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load sales analytics");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const forecastChart = useMemo(() => {
    const hist = timeline.slice(-30).map((r) => ({ date: r.date, actual: r.sales_usd, band_low: null, band_high: null, forecast: null }));
    const proj = (forecast.forecast || []).map((r) => ({
      date: r.date, actual: null, band_low: r.usd_low, band_high: r.usd_high, forecast: r.usd,
    }));
    return [...hist, ...proj];
  }, [timeline, forecast]);

  const cohortColor = (pct) => {
    if (pct >= 40) return "bg-emerald-500/70";
    if (pct >= 20) return "bg-emerald-500/40";
    if (pct >= 10) return "bg-amber-500/40";
    if (pct >= 5)  return "bg-amber-500/20";
    return "bg-white/[0.04]";
  };

  return (
    <div className="space-y-6" data-testid="sales-tab">
      {/* Traffic Trend — Marketing + Sales snapshot */}
      <TrafficTrendChart timeline={trafficTimeline} salesTimeline={{ timeline }} />

      {/* Header KPIs */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="sales-kpi-row">
        <KpiTile label="Today" value={fmtUsd(kpis?.today?.usd)} sub={`${fmtInt(kpis?.today?.orders)} orders`} tone="cyan" testId="kpi-today" />
        <KpiTile label="WTD"   value={fmtUsd(kpis?.wtd?.usd)}   tone="cyan" testId="kpi-wtd" />
        <KpiTile label="MTD"   value={fmtUsd(kpis?.mtd?.usd)}   tone="cyan" testId="kpi-mtd" />
        <KpiTile label="YTD"   value={fmtUsd(kpis?.ytd?.usd)}   sub={`YoY ${kpis?.yoy_pct ?? 0}%`} tone={((kpis?.yoy_pct ?? 0) >= 0) ? "green" : "red"} testId="kpi-ytd" />
        <KpiTile label="MRR"   value={fmtUsd(kpis?.mrr)}        sub={`ARR ${fmtUsd(kpis?.arr)}`} tone="green" testId="kpi-mrr" />
        <KpiTile label="All-time" value={fmtUsd(kpis?.alltime?.usd)} sub={`${fmtInt(kpis?.alltime?.orders)} orders`} tone="gold" testId="kpi-alltime" />
        <KpiTile label="Conv. Rate" value={`${conversion.conversion_pct}%`} sub={`${fmtInt(conversion.paying_customers)} / ${fmtInt(conversion.visitors)}`} tone="cyan" testId="kpi-conversion" />
      </section>

      {/* Sales-over-time + Product Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard icon={LineIcon} title="Sales Over Time" testId="sales-timeline-section">
          <div className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                <XAxis dataKey="date" tick={{ fill: "#555", fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fill: CYAN, fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(1) + 'k' : Math.round(v)}`} />
                <Tooltip contentStyle={{ backgroundColor: "#111113", border: "1px solid #333", fontSize: 12 }} formatter={(v) => fmtUsd(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="sales_usd" stroke={CYAN}  strokeWidth={2}   dot={false} name="Total Sales" />
                <Line type="monotone" dataKey="subs_usd"  stroke={GREEN} strokeWidth={1.5} dot={false} name="Subs" />
                <Line type="monotone" dataKey="coin_usd"  stroke={GOLD}  strokeWidth={1.5} dot={false} name="Coin Packs" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard icon={PieIcon} title="Product Mix" testId="product-mix-section">
          {productMix.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={productMix.slice(0, 10)} dataKey="usd" nameKey="label" innerRadius={45} outerRadius={90} stroke="#0a0a0a">
                  {productMix.slice(0, 10).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#111113", border: "1px solid #333", fontSize: 12 }} formatter={(v) => fmtUsd(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-white/25">
              {loading ? "Loading…" : "No data in range"}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Top Products + Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={ShoppingBag} title="Top Selling Products" testId="top-products-section">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-white/40">
              <tr className="text-left border-b border-white/[0.06]">
                <th className="pb-2">Product</th>
                <th className="pb-2 text-right">Units</th>
                <th className="pb-2 text-right">$</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.length ? topProducts.map((p) => (
                <tr key={p.sku} className="border-b border-white/[0.03]" data-testid={`top-product-row-${p.sku}`}>
                  <td className="py-2 text-white/80">{p.sku}</td>
                  <td className="py-2 text-right font-mono text-white/70">{fmtInt(p.units)}</td>
                  <td className="py-2 text-right font-mono text-cyan-300">{fmtUsd(p.usd)}</td>
                </tr>
              )) : (
                <tr><td colSpan="3" className="py-6 text-center text-white/25">No sales in range</td></tr>
              )}
            </tbody>
          </table>
        </SectionCard>

        <SectionCard icon={Users} title="Top Customers (all-time)" testId="top-customers-section">
          <div className="overflow-y-auto max-h-[340px]">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-white/40 sticky top-0 bg-[#111113]">
                <tr className="text-left border-b border-white/[0.06]">
                  <th className="pb-2">Customer</th>
                  <th className="pb-2 text-right">$</th>
                  <th className="pb-2 text-right">Orders</th>
                  <th className="pb-2 text-right">Last</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.length ? topCustomers.map((c) => (
                  <tr key={c.user_id} className="border-b border-white/[0.03]" data-testid={`top-customer-row-${c.user_id}`}>
                    <td className="py-2">
                      <div className="text-white/80 truncate max-w-[140px]">{c.name}</div>
                      <div className="text-[10px] text-white/40 truncate max-w-[180px]">{c.email}</div>
                    </td>
                    <td className="py-2 text-right font-mono text-cyan-300">{fmtUsd(c.usd)}</td>
                    <td className="py-2 text-right font-mono text-white/70">{fmtInt(c.orders)}</td>
                    <td className="py-2 text-right text-[10px] text-white/50">
                      {c.last_purchase ? new Date(c.last_purchase).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="py-6 text-center text-white/25">No customers yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Forecast */}
      <SectionCard icon={TrendingUp} title="Sales Forecast (14-day projection)" testId="sales-forecast-section">
        <div className="text-xs text-white/40 mb-2">
          Based on 30-day rolling average: <span className="text-white/80 font-mono">{fmtUsd(forecast.avg_daily)}/day</span>
          {" "}· 1σ band <span className="text-white/60 font-mono">±{fmtUsd(forecast.std)}</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={forecastChart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="date" tick={{ fill: "#555", fontSize: 11 }} tickFormatter={d => d.slice(5)} />
            <YAxis tick={{ fill: CYAN, fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(1) + 'k' : Math.round(v)}`} />
            <Tooltip contentStyle={{ backgroundColor: "#111113", border: "1px solid #333", fontSize: 12 }} formatter={(v) => fmtUsd(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="band_high" stroke="none" fill={CYAN} fillOpacity={0.08} name="Upper band" />
            <Area type="monotone" dataKey="band_low"  stroke="none" fill="#0a0a0a" fillOpacity={1} name="" />
            <Line type="monotone" dataKey="actual" stroke={CYAN} strokeWidth={2} dot={false} name="Actual" />
            <Line type="monotone" dataKey="forecast" stroke={GOLD} strokeWidth={2} strokeDasharray="5 5" dot={false} name="Forecast" />
          </AreaChart>
        </ResponsiveContainer>
      </SectionCard>

      {/* Refunds + Churn */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={AlertTriangle} title="Refunds & Chargebacks" testId="refunds-section">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <KpiTile label="Total refunded" value={fmtUsd(refunds.total_usd)} tone="red" testId="refunds-total" />
            <KpiTile label="Events" value={fmtInt(refunds.timeline.length)} tone="amber" testId="refunds-events" />
          </div>
          {refunds.top_refunders.length ? (
            <div className="overflow-y-auto max-h-[200px]">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-white/40 sticky top-0 bg-[#111113]">
                  <tr className="text-left border-b border-white/[0.06]">
                    <th className="pb-2">Customer</th>
                    <th className="pb-2 text-right">Refunded</th>
                    <th className="pb-2 text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.top_refunders.map((r) => (
                    <tr key={r.customer} className="border-b border-white/[0.03]">
                      <td className="py-2 text-white/70 font-mono text-[11px]">{r.customer}</td>
                      <td className="py-2 text-right font-mono text-red-300">{fmtUsd(r.usd)}</td>
                      <td className="py-2 text-right font-mono text-white/70">{fmtInt(r.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-white/30 text-sm py-6 text-center">No refunds in range — clean books ✨</div>
          )}
        </SectionCard>

        <SectionCard icon={AlertTriangle} title="Churn Signal (last 30d)" testId="churn-section">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <KpiTile label="Cancelled / scheduled" value={fmtInt(churn.cancelled_or_scheduled)} tone="amber" testId="churn-count" />
            <KpiTile label="MRR at risk" value={fmtUsd(churn.mrr_at_risk_usd)} tone="red" testId="churn-mrr-risk" />
          </div>
          {churn.at_risk.length ? (
            <div className="overflow-y-auto max-h-[200px]">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-white/40 sticky top-0 bg-[#111113]">
                  <tr className="text-left border-b border-white/[0.06]">
                    <th className="pb-2">Customer</th>
                    <th className="pb-2">Tier / Cycle</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {churn.at_risk.map((c) => (
                    <tr key={c.user_id} className="border-b border-white/[0.03]">
                      <td className="py-2">
                        <div className="text-white/80 truncate max-w-[140px]">{c.name}</div>
                        <div className="text-[10px] text-white/40 truncate max-w-[180px]">{c.email}</div>
                      </td>
                      <td className="py-2 text-[11px] text-white/70">{c.tier} · {c.cycle}</td>
                      <td className="py-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {c.cancel_at_period_end ? "cancels at period end" : c.status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-white/30 text-sm py-6 text-center">No active churn signals</div>
          )}
        </SectionCard>
      </div>

      {/* LTV by source + Cohort */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={Activity} title="LTV by Acquisition Source" testId="ltv-section">
          {ltv.length ? (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-white/40">
                <tr className="text-left border-b border-white/[0.06]">
                  <th className="pb-2">Source</th>
                  <th className="pb-2 text-right">Customers</th>
                  <th className="pb-2 text-right">Total $</th>
                  <th className="pb-2 text-right">Avg LTV</th>
                </tr>
              </thead>
              <tbody>
                {ltv.map((s) => (
                  <tr key={s.source} className="border-b border-white/[0.03]">
                    <td className="py-2 text-white/80 capitalize">{s.source}</td>
                    <td className="py-2 text-right font-mono text-white/70">{fmtInt(s.customers)}</td>
                    <td className="py-2 text-right font-mono text-cyan-300">{fmtUsd(s.total_usd)}</td>
                    <td className="py-2 text-right font-mono text-emerald-300">{fmtUsd(s.ltv_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-white/30 text-sm py-6 text-center">No paying customers to segment yet</div>
          )}
        </SectionCard>

        <SectionCard icon={Users} title="Cohort Retention (last 8 weeks)" testId="cohort-section">
          {cohorts.length ? (
            <div className="space-y-1.5">
              {cohorts.map((c) => (
                <div key={c.cohort} className="flex items-center gap-2 text-xs">
                  <div className="w-16 text-white/50 font-mono">{c.cohort}</div>
                  <div className="w-20 text-white/60 text-right">{c.size} signups</div>
                  <div className={`flex-1 h-6 rounded ${cohortColor(c.conversion_pct)} relative overflow-hidden`}>
                    <div className="absolute inset-0 flex items-center justify-center text-[11px] font-mono text-white/90">
                      {c.converted} paid · {c.conversion_pct}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-white/30 text-sm py-6 text-center">Not enough signup history yet</div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
